/**
 * @file AdminSettings.jsx — /cx/configuracion
 */
import React, { useState, useEffect } from "react";
import { adminService } from "../services/admin";
import { ADMIN_STRINGS } from "../constants/strings";
import { Button, Card, CardHeader, Input, Spinner, Switch, toast } from "../ui";
import "./AdminGeneric.css";

const SETTING_DEFS = [
  {
    key: "maintenanceMode",
    label: "Modo mantenimiento",
    type: "boolean",
    category: "general",
    description: "Cuando está activo, los usuarios no pueden crear nuevas citas",
  },
  {
    key: "maxAppointmentsPerDay",
    label: "Máximo de citas por día",
    type: "number",
    category: "general",
    description: "Límite de citas que un profesional puede recibir por día",
  },
  {
    key: "reminderEnabled",
    label: "Recordatorios activos",
    type: "boolean",
    category: "notifications",
    description: "Enviar recordatorios de citas por WhatsApp",
  },
  {
    key: "reminderHoursBefore",
    label: "Horas antes del recordatorio",
    type: "number",
    category: "notifications",
    description: "Cuántas horas antes de la cita se envía el recordatorio",
  },
];

export default function AdminSettings() {
  const [settings, setSettings] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await adminService.getSettings();
        const map = {};
        (res.data || []).forEach((s) => {
          map[s.key] = s.value;
        });
        setSettings(map);
      } catch {
        // ignore
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSave = async (key) => {
    try {
      setSaving((prev) => ({ ...prev, [key]: true }));
      await adminService.updateSetting(key, settings[key]);
      toast.success(ADMIN_STRINGS.SETTINGS_SAVED);
    } catch {
      // ignore
    } finally {
      setSaving((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleChange = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  if (isLoading) {
    return (
      <div className="ag-page">
        <div className="ag-loading">
          <Spinner size={28} />
        </div>
      </div>
    );
  }

  const generalSettings = SETTING_DEFS.filter((s) => s.category === "general");
  const notifSettings = SETTING_DEFS.filter((s) => s.category === "notifications");

  const renderSetting = (def) => (
    <div key={def.key} className="ax-setting">
      <div className="ax-setting-text">
        <span className="ax-setting-label">{def.label}</span>
        <span className="ax-setting-hint">{def.description}</span>
      </div>
      <div className="ax-setting-control">
        {def.type === "boolean" ? (
          <Switch
            checked={!!settings[def.key]}
            onCheckedChange={(checked) => handleChange(def.key, !!checked)}
          />
        ) : (
          <Input
            type="number"
            value={settings[def.key] ?? ""}
            onChange={(e) => handleChange(def.key, parseInt(e.target.value) || 0)}
            fullWidth={false}
            style={{ width: 100 }}
          />
        )}
        <Button
          size="sm"
          onClick={() => handleSave(def.key)}
          loading={saving[def.key]}
        >
          {ADMIN_STRINGS.SETTINGS_SAVE}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="ag-page">
      <header className="ag-head">
        <span className="ag-eyebrow">Admin · Configuración</span>
        <h1 className="ag-title">{ADMIN_STRINGS.SETTINGS_TITLE}</h1>
      </header>

      <div className="ax-settings-grid">
        <Card>
          <CardHeader title={ADMIN_STRINGS.SETTINGS_GENERAL} />
          <div className="ax-settings-list">{generalSettings.map(renderSetting)}</div>
        </Card>
        <Card>
          <CardHeader title={ADMIN_STRINGS.SETTINGS_NOTIFICATIONS} />
          <div className="ax-settings-list">{notifSettings.map(renderSetting)}</div>
        </Card>
      </div>
    </div>
  );
}
